import { colors } from '@apollo/brand';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import IconClient from '@apollo/icons/default/IconClient.svg';
import {
  Accordion,
  AccordionHeader,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AccordionTitle,
  Alert,
  Badge,
  Button,
  Code,
  Group,
  Heading,
  Spinner,
  customColors,
} from '@apollo/orbit';
import { Telescope } from '@apollo/space-kit/illustrations/Telescope';
import classnames from 'classnames';
import _ from 'lodash';
import React, { ReactNode, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import {
  insightsRouteConfig,
  variantSettingsRouteConfig,
} from 'src/app/graph/routes';
import { StatsTimeRangeResult } from 'src/components/affectedOperations/affectedOperationsHelpers';
import { CursorProvider } from 'src/components/charts/helpers/Cursor';
import { LineChart } from 'src/components/charts/LineChart';
import { ContactSupportLink } from 'src/components/ContactSupportCTAs';
import { MomentTimestamp } from 'src/components/timestamp/Timestamp';
import Config from 'src/lib/config';
import { date, findReasonableResolution } from 'src/lib/date/date';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { semverSort } from 'src/lib/semver';

import { ChangeList } from '../changeList/ChangeList';
import {
  getAffectedOperationStatusBadge,
  getOperationStatus,
  ignoreOperationsMutation,
} from '../operationsCheckList/OperationsCheckList';

import { checkOperationsDetailsQuery } from './LegacyCheckOperationDetails';
import { MarkedAsSafeOrIgnoredAlert } from './MarkedAsSafeOrIgnoredAlert';
import { ConfirmationModal } from './overrideButton/confirmationModal/ConfirmationModal';
import {
  ConfirmationAction,
  OverrideButton,
} from './overrideButton/OverrideButton';

interface Props {
  graphId: string;
  graphVariant: string;
  checkId: string;
  operation: GraphQLTypes.CheckOperationDetailsAffectedQueryFragment;
  statsWindow: StatsTimeRangeResult;
  canViewClientVersions: boolean;
}

// We don't actually use the returned data for these mutations since the cache
// is updated to force a refetch, but we need to select something, so we just
// select __typename.
export const unmarkAsSafeMutation = gql`
  mutation UnmarkAsSafeMutation(
    $graphId: ID!
    $checkId: ID!
    $operationId: ID!
  ) {
    service(id: $graphId) {
      unmarkChangesForOperationAsSafe(
        checkID: $checkId
        operationID: $operationId
      ) {
        __typename
      }
    }
  }
`;
export const unignoreOperationsMutation = gql`
  mutation UnignoreOperationsMutation($graphId: ID!, $operationIds: [ID!]!) {
    service(id: $graphId) {
      unignoreOperationsInChecks(ids: $operationIds) {
        __typename
      }
    }
  }
`;
export const markAsSafeMutation = gql`
  mutation MarkAsSafeMutation($graphId: ID!, $checkId: ID!, $operationId: ID!) {
    service(id: $graphId) {
      markChangesForOperationAsSafe(
        checkID: $checkId
        operationID: $operationId
      ) {
        __typename
      }
    }
  }
`;

function groupClientsByNameToVersions(
  queryStats: GraphQLTypes.CheckOperationsDetailsQuery_service_perClientStats_queryStats[],
): Record<string, string[]> {
  const clientsGroupedByNameDictionary = _.groupBy(
    queryStats,
    (stat) => stat.groupBy.clientName ?? '',
  );
  const clientVersionsGroupedByName = _.mapValues(
    clientsGroupedByNameDictionary,
    (queryStatsForClient) =>
      semverSort(
        _.uniq(
          queryStatsForClient.map((stat) => stat.groupBy.clientVersion ?? ''),
        ),
      ),
  );
  return clientVersionsGroupedByName;
}

const OperationDetailsSection = ({
  children,
  title,
  linkToConfig,
  loading,
}: {
  children: React.ReactNode;
  title?: ReactNode;
  linkToConfig?: React.ReactNode;
  loading?: boolean;
}) => (
  <div className="my-4">
    <div className="flex justify-between">
      {title && (
        <Heading variant="heading" size="lg">
          {title}
        </Heading>
      )}
      {linkToConfig}
    </div>
    {loading ? (
      <div className="flex h-32 w-full items-center justify-center">
        <Spinner />
      </div>
    ) : (
      <div className="py-4">{children}</div>
    )}
  </div>
);

export const OperationCheckDetails = ({
  graphId,
  graphVariant,
  checkId,
  operation,
  statsWindow,
  canViewClientVersions,
}: Props) => {
  const client = useApolloClient();

  // After calling these mutations, we need to evict the old
  // ignored/approved flags to cause that data to be re-fetched.
  const evictCache = () => {
    client.cache.evict({
      id: `AffectedQuery:${operation.id}`,
      fieldName: 'alreadyIgnored',
    });
    client.cache.evict({
      id: `AffectedQuery:${operation.id}`,
      fieldName: 'alreadyApproved',
    });
    client.cache.gc();
  };

  const [markAsSafe, { loading: markAsSafeLoading, error: markAsSafeError }] =
    useMutation<
      GraphQLTypes.MarkAsSafeMutation,
      GraphQLTypes.MarkAsSafeMutationVariables
    >(markAsSafeMutation, {
      variables: { graphId, checkId, operationId: operation.id },
      onCompleted: () => evictCache(),
    });

  const [ignoreOperations, { loading: ignoreLoading, error: ignoreError }] =
    useMutation<
      GraphQLTypes.IgnoreOperationsMutation,
      GraphQLTypes.IgnoreOperationsMutationVariables
    >(ignoreOperationsMutation, {
      variables: { graphId, operationIds: [operation.id] },
      onCompleted: () => evictCache(),
    });

  const [
    unmarkAsSafe,
    { loading: unmarkAsSafeLoading, error: unmarkAsSafeError },
  ] = useMutation<
    GraphQLTypes.UnmarkAsSafeMutation,
    GraphQLTypes.UnmarkAsSafeMutationVariables
  >(unmarkAsSafeMutation, {
    variables: { graphId, checkId, operationId: operation.id },
    onCompleted: () => evictCache(),
  });

  const [
    unignoreOperations,
    { loading: unignoreLoading, error: unignoreError },
  ] = useMutation<
    GraphQLTypes.UnignoreOperationsMutation,
    GraphQLTypes.UnignoreOperationsMutationVariables
  >(unignoreOperationsMutation, {
    variables: { graphId, operationIds: [operation.id] },
    onCompleted: () => evictCache(),
  });

  const [confirmAction, setConfirmAction] = useState<ConfirmationAction | null>(
    null,
  );

  const location = useLocation();
  const timeFrom = statsWindow.queryableTimeRange?.timeFrom ?? '-86400';
  const timeTo = statsWindow.queryableTimeRange?.timeTo ?? '-0';
  if (!checkId) {
    throw new Error('expected to be loaded on a route with checkId');
  }

  const diffSeconds = date.getMinDifference(timeFrom, timeTo) * 60;
  const resolution = findReasonableResolution(diffSeconds);
  const {
    data,
    loading,
    error: operationDetailsError,
  } = useQuery<
    GraphQLTypes.CheckOperationsDetailsQuery,
    GraphQLTypes.CheckOperationsDetailsQueryVariables
  >(checkOperationsDetailsQuery, {
    variables: {
      graphId,
      timeFrom,
      timeTo,
      resolution,
      filter: {
        queryId: operation.id,
      },
    },
  });

  const clientVersionsGroupedByName = Object.entries(
    groupClientsByNameToVersions(
      data?.service?.perClientStats?.queryStats ?? [],
    ),
  );

  const chartData = useMemo(
    () => [
      {
        colorGradientTop: `${colors.primitives.blue[300]}3C`,
        colorGradientBottom: `${colors.primitives.blue[300]}00`,
        primaryColor: customColors.flowchart.d,
        cursorColor: customColors.brand.accent,
        unit: ' RPM',
        data: data?.service?.usageStats?.queryStats ?? [],
        getValue: (
          stats: GraphQLTypes.CheckOperationsDetailsQuery_service_usageStats_queryStats,
        ) => ({
          timestamp: stats.timestamp,
          value:
            stats.metrics.totalRequestCount /
            date.getMinDifference(
              stats.timestamp,
              date.incrementByResolution(stats.timestamp, resolution),
            ),
        }),
      },
    ],
    [data?.service?.usageStats?.queryStats, resolution],
  );

  const { currentVariantRouteConfig } = useInternalGraphLinking();
  const status = getOperationStatus(operation);
  const operationStatusBadge = getAffectedOperationStatusBadge(
    status,
    'rounded',
  );
  const exactlyOneChange = operation?.changes?.length === 1;

  const overrideError = markAsSafeError || ignoreError;
  const undoError = unmarkAsSafeError || unignoreError;

  return (
    <>
      {(operation.alreadyApproved || operation.alreadyIgnored) && (
        <MarkedAsSafeOrIgnoredAlert
          graphId={graphId}
          operation={operation}
          loading={unmarkAsSafeLoading || unignoreLoading}
          onUndoClick={() => {
            if (operation.alreadyApproved) {
              setConfirmAction('unmarkAsSafe');
            }
            if (operation.alreadyIgnored) {
              setConfirmAction('unignore');
            }
          }}
        />
      )}
      {(overrideError || undoError) && (
        <Alert status="error">
          Error {overrideError ? 'overriding operation' : 'undoing override'}.
          Please try again or{' '}
          <ContactSupportLink>contact support for help.</ContactSupportLink>
        </Alert>
      )}
      <OperationDetailsSection title="Status">
        <div className="flex flex-wrap items-center">
          {operationStatusBadge}
          {/* OverrideButton should only be visible if operation has not already been marked as safe or ignored */}
          {!operation.alreadyApproved && !operation.alreadyIgnored && (
            <OverrideButton
              graphId={graphId}
              exactlyOneChange={exactlyOneChange}
              loading={markAsSafeLoading || ignoreLoading}
              markAsSafe={markAsSafe}
              ignoreOperations={ignoreOperations}
              buttonSize="md"
              exactlyOneOperation={true}
            />
          )}
        </div>
      </OperationDetailsSection>
      {operationDetailsError ||
      data?.service?.usageStats?.queryStats.length === 0 ? (
        <div className="flex h-32 w-full flex-col items-center justify-center">
          <Telescope />
          <div className="pt-3 text-base text-secondary">
            {!statsWindow.queryableTimeRange && statsWindow.errorMessage ? (
              <span>
                No usage for operation in the past{' '}
                {statsWindow.maxRangeInDaysForChecks} day(s).{' '}
                <ContactSupportLink>
                  Contact us to request a longer retention window.
                </ContactSupportLink>
              </span>
            ) : (
              <span>
                Missing usage data for check.{' '}
                <ContactSupportLink>
                  Contact us if this problem persists.
                </ContactSupportLink>
              </span>
            )}
          </div>
        </div>
      ) : (
        <>
          <OperationDetailsSection title="Impacting Changes">
            <div className="pt-2 text-base">
              {operation.changes && operation.changes.length > 0 ? (
                <div className="overflow-x-auto">
                  <ChangeList changes={operation.changes} />
                </div>
              ) : (
                <span className="text-secondary">
                  No impacting changes found. Check statuses are maintained
                  forever, but details may be lost after 30 days.
                </span>
              )}
            </div>
          </OperationDetailsSection>
          <OperationDetailsSection
            title={
              <div className="flex">
                Request Rate (RPM) From
                <div className="ml-1">
                  <MomentTimestamp value={timeFrom} /> -{' '}
                  <MomentTimestamp value={timeTo} />
                </div>
              </div>
            }
            loading={loading}
          >
            <CursorProvider>
              <LineChart
                stacked
                stacks={chartData}
                className="h-48"
                resolution={resolution}
              />
            </CursorProvider>
          </OperationDetailsSection>
          <OperationDetailsSection
            title={
              <span>
                Used By Client
                {clientVersionsGroupedByName.length !== 1 ? 's' : ''}
              </span>
            }
            loading={loading}
            linkToConfig={
              <Button
                variant="buttonLink"
                as={Link}
                to={variantSettingsRouteConfig.locationFrom({
                  location,
                  patch: { tab: 'checks_operations', graphVariant },
                  fromRouteConfig: currentVariantRouteConfig,
                })}
              >
                Edit in Configuration
              </Button>
            }
          >
            {clientVersionsGroupedByName.map(([name, versions]) => (
              <Accordion allowToggle key={name} className="my-3">
                <AccordionItem>
                  <AccordionHeader>
                    <Group>
                      <AccordionTitle>
                        {name ? (
                          <div className="flex items-center">
                            <IconClient />
                            <span className="mx-2">{name}</span>
                            <span>{`(${versions.length} version${
                              versions.length > 1 ? 's' : ''
                            })`}</span>
                          </div>
                        ) : (
                          <span className="italic">Unidentified clients</span>
                        )}
                      </AccordionTitle>
                      {!name && (
                        <Badge variant="neutralOutlined">Undefined</Badge>
                      )}
                    </Group>
                    {name && canViewClientVersions && <AccordionIcon />}
                  </AccordionHeader>
                  {name && canViewClientVersions && (
                    <AccordionPanel>
                      {versions.map((version) => {
                        return (
                          <Code
                            className={classnames('m-1', {
                              italic: version === '',
                            })}
                            key={version}
                          >
                            {version || 'Unspecified version'}
                          </Code>
                        );
                      })}
                    </AccordionPanel>
                  )}
                </AccordionItem>
              </Accordion>
            ))}
          </OperationDetailsSection>
          <OperationDetailsSection
            title={
              <span>
                Used By Variant
                {(data?.service?.perVariantStats?.queryStats ?? []).length !== 1
                  ? 's'
                  : ''}
              </span>
            }
            loading={loading}
            linkToConfig={
              <Button
                variant="buttonLink"
                as={Link}
                to={variantSettingsRouteConfig.locationFrom({
                  location,
                  patch: { tab: 'checks_operations' },
                  fromRouteConfig: currentVariantRouteConfig,
                })}
              >
                Edit in Configuration
              </Button>
            }
          >
            {(data?.service?.perVariantStats?.queryStats ?? []).map((stat) => (
              <div key={stat.groupBy.schemaTag ?? ''} className="flex pt-3">
                <Button
                  variant="buttonLink"
                  as={Link}
                  to={insightsRouteConfig.path({
                    graphId,
                    query: operation.id,
                    queryName: operation.name || undefined,
                    graphVariant:
                      stat.groupBy.schemaTag || Config.service.defaultVariant,
                    range: 'custom',
                    from: timeFrom,
                    to: timeTo,
                  })}
                >
                  {data?.service?.title} /{' '}
                  {stat.groupBy.schemaTag || Config.service.defaultVariant}
                </Button>
              </div>
            ))}
          </OperationDetailsSection>
        </>
      )}
      <ConfirmationModal
        isOpen={confirmAction === 'unmarkAsSafe'}
        title={`Unmark change${exactlyOneChange ? '' : 's'} as safe?`}
        description={
          <>
            By reverting the approval for this operation, you acknowledge that{' '}
            {exactlyOneChange ? 'this change' : 'these changes'} may be marked
            as breaking this operation in future checks.
          </>
        }
        onSubmit={() => unmarkAsSafe()}
        onClose={() => setConfirmAction(null)}
      />
      <ConfirmationModal
        isOpen={confirmAction === 'unignore'}
        title="No longer ignore this operation?"
        description={
          <>
            By no longer ignoring this operation, you acknowledge that changes
            affecting this operation should affect the outcome of future checks.
          </>
        }
        onSubmit={() => unignoreOperations()}
        onClose={() => setConfirmAction(null)}
      />
    </>
  );
};
